import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

/** Marks the one element being printed; the print stylesheet hides everything else. */
export const RECEIPT_PRINTING_ATTRIBUTE = 'data-receipt-printing';

/** CSS pixels are 1/96 in, on screen and on paper alike. */
const MM_PER_PX = 25.4 / 96;

/** Rounding slack, so the last line never spills onto a second page. */
const PAGE_SLACK_MM = 2;

/**
 * The stylesheet in force while a receipt prints.
 *
 * - **The page is the receipt.** As wide as the printer's setting and exactly as long as the
 *   slip, because a roll has no page length: a fixed one would cut a long order into several
 *   slips and feed blank paper after a short one.
 * - **Nothing else prints.** Every element that neither is the receipt, lies inside it, nor
 *   contains it is hidden, and its ancestors lose their margins, padding and positioning — so
 *   it works under any shell layout without naming a single part of it.
 */
export function receiptPrintCss(widthCm: number, heightMm: number): string {
  const sheet = `[${RECEIPT_PRINTING_ATTRIBUTE}]`;
  return `
@page { size: ${widthCm}cm ${heightMm}mm; margin: 0; }
@media print {
  html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
  body :not(${sheet}):not(${sheet} *):not(:has(${sheet})) { display: none !important; }
  body :has(${sheet}) {
    display: block !important; position: static !important; float: none !important;
    margin: 0 !important; padding: 0 !important; border: 0 !important;
    width: auto !important; min-width: 0 !important; max-width: none !important;
    height: auto !important; min-height: 0 !important; overflow: visible !important;
    transform: none !important; box-shadow: none !important; background: none !important;
  }
  ${sheet} { margin: 0 !important; }
}`;
}

/**
 * Prints one receipt through the browser's own print dialog (#205).
 *
 * The browser dialog rather than a library: the old page used `ngx-print`, which copies the
 * section into a pop-up window with every stylesheet of the app — a second document to keep in
 * step, and a pop-up for the blocker to stop. Here the page prints itself, under a stylesheet
 * that lives exactly as long as the print.
 */
@Injectable({ providedIn: 'root' })
export class ReceiptPrinter {
  private readonly document = inject(DOCUMENT);

  /**
   * Prints `sheet` alone, `widthCm` wide. Emits once and completes when the print dialog has
   * closed — whether the user printed or cancelled, which no browser tells a page.
   *
   * The length is measured on screen before the dialog opens: the slip is laid out at its paper
   * width in both media, so it is as long on paper as in the preview.
   */
  print(sheet: HTMLElement, widthCm: number): Observable<void> {
    return new Observable<void>((subscriber) => {
      const view = this.document.defaultView;
      const heightMm = Math.ceil(sheet.getBoundingClientRect().height * MM_PER_PX) + PAGE_SLACK_MM;
      const style = this.document.createElement('style');
      style.textContent = receiptPrintCss(widthCm, heightMm);

      const cleanUp = () => {
        view.removeEventListener('afterprint', done);
        style.remove();
        sheet.removeAttribute(RECEIPT_PRINTING_ATTRIBUTE);
      };
      // `afterprint` rather than the line after print(): Chrome blocks in print(), but not
      // every browser does, and tearing the stylesheet down early would print the whole page.
      const done = () => {
        cleanUp();
        subscriber.next();
        subscriber.complete();
      };

      sheet.setAttribute(RECEIPT_PRINTING_ATTRIBUTE, '');
      this.document.head.appendChild(style);
      view.addEventListener('afterprint', done);
      view.print();

      return cleanUp;
    });
  }
}
